import Sprite from "app/components/sprite";
import { ZBNI_CUSTOMER_SUPPORT } from "app/config/constants";
import { useAnimation } from "app/hooks/useAnimation";
import { useI18nLanguage } from "app/hooks/i18n";
import amazon from "app/images/section-images/features/amazon-round.png";
import arabicMobilePaymentGateways from "app/images/section-images/features/arabic/mobile-payment-gateways.png";
import arabicPaymentGateways from "app/images/section-images/features/arabic/payment-gateways.png";
import ayden from "app/images/section-images/features/ayden.png";
import checkout from "app/images/section-images/features/checkout.png";
import mobilePaymentGateways from "app/images/section-images/features/mobile-payment-gateways.png";
import paymentGateways from "app/images/section-images/features/payment-gateways.png";
import stripe from "app/images/section-images/features/stripe-round.png";
import { useRef } from "react";
import { Trans } from "react-i18next";

const TextContent = ({ title, body, button }) => (
  <div className="relative z-[1]">
    <div
      className="mt-16 max-w-sm sm:mt-16 ltr:md:ml-[5%] rtl:md:mr-[5%] lg:-mt-32
      lg:max-w-none ltr:lg:ml-[35%] rtl:lg:mr-[35%] ltr:xl:ml-48 rtl:xl:mr-48"
    >
      <h3
        className="animate__animated mt-4 text-[22px] text-base font-extrabold
        leading-[26px] tracking-wide text-white opacity-0"
        ref={title}
        data-animation="animate__fadeInDown"
      >
        <Trans>Already accepting payments online?</Trans>
      </h3>
      <p
        className="animate__animated mt-4 max-w-[460px] text-base
        font-normal leading-[24px] text-white opacity-0"
        ref={body}
        data-animation="animate__fadeInDown"
      >
        <Trans>
          Connect your existing payment provider to Zbooni. Empower your
          business to sell more and add friendlier ways for your customers to
          check out.
        </Trans>
      </p>
      <a
        href={`${ZBNI_CUSTOMER_SUPPORT}&text=Hello%21%20I%20am%20interested%20in%20learning%20more%20about%20Zbooni.%20Can%20you%20give%20me%20additional%20information%20about%20Zbooni%27s%20features%3F`}
        target="_blank"
        rel="noreferrer"
        id="cta_acceptPayments_integratedPayments_getInTouch"
      >
        <button
          className="green-gradient-btn animate__animated
          mt-10 opacity-0 md:mt-16"
          ref={button}
          data-animation="animate__fadeInDown"
        >
          <p>
            <Trans>Get In touch</Trans>
          </p>
        </button>
      </a>
    </div>
  </div>
);

export const IntegratedPaymentsSection = () => {
  const title = useRef(null);
  const body = useRef(null);
  const button = useRef(null);
  const card = useRef(null);
  const logo1 = useRef(null);
  const logo2 = useRef(null);
  const logo3 = useRef(null);
  const logo4 = useRef(null);
  const vector = useRef(null);

  useAnimation([title, body, button, card, logo1, logo2, logo3, logo4, vector]);

  const { locale: language } = useI18nLanguage();

  return (
    <section
      className="relative w-full overflow-hidden
      bg-primary text-white sm:overflow-visible"
    >
      <div className="zbooni-layout">
        <div className="relative">
          <Sprite
            svgRef={vector}
            dataAnimation="animate__fadeIn animate__delay-2s"
            name="gray-diamond"
            className="animate__animated absolute -top-10 z-[0] h-[538px]
            w-[521px] ltr:-left-[25%] rtl:-right-[25%] ltr:sm:left-12
            rtl:sm:right-12 md:h-[550px] ltr:md:left-20 rtl:md:right-20
            ltr:lg:left-40 rtl:lg:right-40"
          />
          <div
            className="relative flex flex-col-reverse items-center
            pb-8 md:flex-row md:pb-20 lg:pb-0"
          >
            <div
              className="relative top-10 z-[10] mb-16 ml-0
              max-w-fit sm:top-10 md:mb-0 ltr:md:ml-4 rtl:md:mr-4
              lg:-top-20 xl:-top-20 ltr:xl:ml-20 rtl:xl:mr-20"
            >
              <img
                ref={card}
                data-animation="animate__fadeInLeft animate__delay-2s"
                src={language == "ar" ? arabicPaymentGateways : paymentGateways}
                className="animate__animated relative top-0
                hidden h-[630px] opacity-0 lg:block"
                alt="payment gateways"
              />
              <img
                src={
                  language == "ar"
                    ? arabicMobilePaymentGateways
                    : mobilePaymentGateways
                }
                className="relative top-0 block h-[520px] lg:hidden"
                alt="payment gateways"
              />
              <img
                ref={logo4}
                data-animation="animate__zoomIn animate__delay-2s"
                src={stripe}
                className="animate__animated absolute h-[66px] opacity-0
                ltr:-left-2 ltr:bottom-20 rtl:bottom-14 md:bottom-20
                lg:bottom-16 lg:h-[128px] ltr:lg:-left-14
                rtl:lg:right-[-52px] ltr:xl:-left-14"
                alt="stripe logo"
              />
              <img
                ref={logo2}
                data-animation="animate__zoomIn animate__delay-4s"
                src={checkout}
                className="animate__animated absolute h-[68px] opacity-0
                ltr:bottom-36 ltr:right-8 rtl:bottom-28 rtl:left-3
                md:bottom-36 ltr:md:right-6 rtl:md:left-6 lg:bottom-40
                lg:h-[101px] ltr:lg:right-0 rtl:lg:left-2"
                alt="checkout logo"
              />
              <img
                ref={logo1}
                data-animation="animate__zoomIn animate__delay-5s"
                src={ayden}
                className="animate__animated absolute h-[42px]
                opacity-0 ltr:bottom-60 ltr:right-2 rtl:bottom-52
                rtl:left-1 md:bottom-[230px] ltr:md:right-0
                rtl:md:-left-2 lg:bottom-[300px] lg:h-[69px]
                ltr:lg:-right-10 rtl:lg:-left-9 ltr:xl:-right-14"
                alt="ayden logo"
              />
              <img
                ref={logo3}
                data-animation="animate__zoomIn animate__delay-3s"
                src={amazon}
                className="animate__animated absolute bottom-12 h-[90px]
                opacity-0 ltr:right-0 rtl:bottom-4 rtl:left-0
                md:bottom-8 lg:h-[140px] ltr:lg:-right-32
                rtl:lg:-left-28 ltr:xl:-right-40 rtl:xl:-left-36"
                alt="amazon logo"
              />
            </div>
            <TextContent title={title} body={body} button={button} />
          </div>
        </div>
      </div>
    </section>
  );
};
